/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'preact/hooks';
import { useFocusWithin } from '../hooks/UNSAFE_useFocusWithin';
import { useUser } from '../hooks/UNSAFE_useUser';
import { isSelectAll } from '../utils/PRIVATE_keyboardUtils';
import { classNames } from '../utils/UNSAFE_classNames';
import { DimensionProps, dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { TextTag } from './TextTag';
import { textTagListStyles } from './themes/TextTagListStyles.css';

/**
 * Data structure of the text tag item
 */
type TextTagItem<V> = {
  label: string;
  value: V;
};

/**
 * Type of the ref handle of the component
 */
type TextTagListHandle = {
  /**
   * Focuses the text tag container
   */
  focus: () => void;

  /**
   * Blurs the text tag container
   */
  blur: () => void;
};

/**
 * Supported style props
 */
const supportedDimensions = ['maxWidth'] as const;
type SupportedDimensions = (typeof supportedDimensions)[number];
type StyleProps = Pick<DimensionProps, SupportedDimensions>;

/**
 * Props for the TextTag component
 */
type Props<V> = StyleProps & {
  /**
   * Specifies accessible screen reader text.
   */
  'aria-label'?: string;

  /**
   * An array of objects which is used to stamp out the TextTag components
   */
  data: TextTagItem<V>[];

  /**
   * Flag to indicate if the label is inside the container
   * TODO: Handle this in a more generic way if/when we make this component generic
   */
  hasInsideLabel?: boolean;

  /**
   * Specifies whether to show the remove icon in the TextTag components
   */
  removeIcon?: 'always' | 'never';

  /**
   * A callback function to be invoked when the component wants to exit the navigation.
   * This happens when one presses Right arrow after reaching the end of the list (or Left
   * arrow in RTL mode).
   */
  onExitNavigation?: () => void;

  /**
   * A callback function to be invoked when one or more text tags are removed
   */
  onRemove?: (items: V[]) => void;
};

/**
 * Setting up interpolations
 */
const supportedInterpolations = Array.from(supportedDimensions, (x) => dimensionInterpolations[x]);
const styleInterpolations = mergeInterpolations<StyleProps>(supportedInterpolations);

/**
 * TextTagList component for rendering the collection of TextTags as well
 * as managing the navigation.
 *
 * @param param0 The props for the TextTagList component
 * @param ref The ref for the component
 */
function _TextTagList<V>(props: Props<V>, ref: Ref<TextTagListHandle> = () => {}) {
  // destructure props
  const {
    'aria-label': ariaLabel,
    data,
    hasInsideLabel = false,
    removeIcon = 'never',
    onExitNavigation,
    onRemove
  } = props;

  // ref for setting the scroll position for the div
  const divRef = useRef<HTMLDivElement>(null);

  // Internal state for storing the current selected tags
  const [selectedTags, setSelectedTags] = useState<V[]>([]);

  // The navigation of focus will depend on the reading direction, so we need to
  // get the current reading direction
  const { direction } = useUser();

  // Create a ref for maintaining the current selected tag. This is used for
  // determining which tag needs to be selected next when pressing ArrowLeft or
  // ArrowRight.
  const currentSelectedTagIndex = useRef(-1);
  // Create a ref that holds the last tag which is needed when receiving focus
  // programmatically.
  const lastTag = data.at(-1)?.value;
  const lastTagMeta = useRef({ value: lastTag, index: data.length - 1 });
  lastTagMeta.current = { value: lastTag, index: data.length - 1 };

  // A function to select the tag at the given index
  const selectTagAtIndex = useCallback(
    (index: number) => {
      const key = data.at(index)?.value;
      if (key == null) return;
      setSelectedTags([key]);
    },
    [data] // setSelectedTags is stable and not needed in dep array
  );
  // A function to clear the selection
  const clearSelection = useCallback(() => {
    setSelectedTags((previousSelectedTags) => {
      currentSelectedTagIndex.current = -1;
      return previousSelectedTags.length ? [] : previousSelectedTags;
    });
  }, []); // setSelectedTags is stable and not needed in dep array

  // Handle the remove & delete events from the tags
  const onTagRemoveIconClick = useCallback(
    (value: V) => {
      // on remove icon click we only need to remove the tag that
      // invoked the event
      onRemove?.([value]);
      onExitNavigation?.();
      // Firefox & Safari are not calling onBlur when focusing out of the
      // tag list through onExitNavigation, so clear the selections explicitly
      // here.
      clearSelection();
    },
    [clearSelection, onExitNavigation, onRemove]
  );
  const onTagRemoveAction = useCallback(
    (value: V) => {
      // on delete action we need to remove the tag that invoked the delete action
      // as well as the selected tags.
      const valuesToRemove = [...selectedTags];
      if (!selectedTags.includes(value)) {
        valuesToRemove.push(value);
      }
      onRemove?.(valuesToRemove);
      onExitNavigation?.();
      // Firefox & Safari are not calling onBlur when focusing out of the
      // tag list through onExitNavigation, so clear the selections explicitly
      // here.
      clearSelection();
    },
    [selectedTags, clearSelection, onExitNavigation, onRemove]
  );

  // Handle the select event from the tags
  const onTagSelect = useCallback(
    (value: V) => {
      setSelectedTags([value]);
      currentSelectedTagIndex.current = data.findIndex((tag) => tag.value === value);
    },
    [data, setSelectedTags]
  );

  // Handle keyDown event on the tags container
  const onTagKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (data.length === 0) return;
      // Move towards the beginning of the list when pressing
      // Left arrow (or Right arrow in rtl).
      if (
        (direction === 'ltr' && event.code === 'ArrowLeft') ||
        (direction === 'rtl' && event.code === 'ArrowRight')
      ) {
        if (currentSelectedTagIndex.current === 0) return;
        currentSelectedTagIndex.current -= 1;
        selectTagAtIndex(currentSelectedTagIndex.current);
        return;
      }

      // Move towards the end of the list when pressing
      // Right arrow (or Left arrow in rtl). If at the end,
      // invoke onExitNavigation.
      if (
        (direction === 'ltr' && event.code === 'ArrowRight') ||
        (direction === 'rtl' && event.code === 'ArrowLeft')
      ) {
        if (currentSelectedTagIndex.current === data.length - 1) {
          // We have reached the end, so exit navigation
          onExitNavigation?.();
          return;
        }
        currentSelectedTagIndex.current += 1;
        selectTagAtIndex(currentSelectedTagIndex.current);
        return;
      }

      // When pressing the Home key, move the focus to the first tag
      if (event.code === 'Home') {
        currentSelectedTagIndex.current = 0;
        selectTagAtIndex(currentSelectedTagIndex.current);
        return;
      }

      // When pressing the End key, move the focus to the last tag
      if (event.code === 'End') {
        currentSelectedTagIndex.current = data.length - 1;
        selectTagAtIndex(currentSelectedTagIndex.current);
        return;
      }

      // If it is a Select All event, then select all the tags and do
      // not move the focus
      if (isSelectAll(event)) {
        setSelectedTags(data.map((tag) => tag.value));
        // prevent default to prevent selection of the whole screen
        event.preventDefault();
        return;
      }
    },
    [data, direction, onExitNavigation, selectTagAtIndex, setSelectedTags]
  );

  const handleOnFocus = useCallback(() => {
    setSelectedTags((previousSelectedTags) => {
      // select the tag only if there are no current selected tags
      // and there is data
      if (previousSelectedTags.length || lastTagMeta.current.value === undefined)
        return previousSelectedTags;

      currentSelectedTagIndex.current = lastTagMeta.current.index;
      return [lastTagMeta.current.value];
    });
  }, []); // setSelectedTags is stable and not needed in the dep array
  const handleOnBlur = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  // When blurring out of the component, remove all selections
  const { focusProps } = useFocusWithin({
    onBlurWithin: handleOnBlur
  });

  // Update the ref to include the handler methods
  useImperativeHandle(
    ref,
    () => ({
      blur: handleOnBlur,
      focus: handleOnFocus
    }),
    [handleOnBlur, handleOnFocus]
  );

  useEffect(() => {
    // every time the data is changed, scroll to the end of the list to show the latest added
    // pill
    // In ltr we need to scroll to the right, so positive value should be passed and in rtl we
    // need to scroll to the left, so negative value should be passed
    divRef.current?.scrollTo({
      left: divRef.current.scrollWidth * (direction === 'ltr' ? 1 : -1)
    });
  }, [data, direction]);

  // the styles for the component
  const styles = styleInterpolations(props);
  // TODO: use theming and multi-variant styles
  // 
  const rootClasses = classNames([
    textTagListStyles.textTagListBase,
    hasInsideLabel && textTagListStyles.textTagListInsideLabel
  ]);

  return (
    <div
      class={rootClasses}
      ref={divRef}
      tabIndex={-1}
      onKeyDown={onTagKeyDown}
      role="listbox"
      aria-label={ariaLabel}
      aria-multiselectable="true"
      style={styles}
      {...focusProps}>
      {data.map((tag, index) => (
        <TextTag
          isFocused={currentSelectedTagIndex.current === index}
          isSelected={selectedTags.includes(tag.value)}
          key={tag.value}
          onRemoveAction={onTagRemoveAction}
          onSelect={onTagSelect}
          onRemoveIconClick={onTagRemoveIconClick}
          removeIcon={removeIcon}
          value={tag.value}>
          {tag.label}
        </TextTag>
      ))}
    </div>
  );
}

export const TextTagList = forwardRef<TextTagListHandle, Props<any>>(_TextTagList);
